// @ts-nocheck
import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Button from "../../../../components/button/button";
import Placeholder from "../../../../components/placeholder/placeholder";
import StatusLabel from "./status-label/status-label";
import { convertDate } from "../../../../core/utils/date.utils";
import { APP_BASE_PATHNAMES } from "../../../../core/consts/consts";
import nexarOneLogo from "../../../../core/assets/nexar-n.png";
import { getStatusConfig, subscriptionPlaceholder } from "./utils";
import styles from "./subscription-card.module.scss";
import { Subscription } from "../../../../contracts/nexar-api/types";
import { useDI } from "../../../../core/di";
import chevronRight from "./chevron-right.svg";

interface Props {
  subscription?: Subscription;
  contentOnly?: boolean;
  className?: string;
}

const Title = ({ children }: PropsWithChildren<{}>): React.ReactElement => {
  const newClassName = clsx(styles.Text, styles.Title);
  return <p className={newClassName}>{children}</p>;
};
const DescriptionItem = ({
  title,
  description,
  expandedDescription,
}: {
  title: ReactNode;
  description?: ReactNode;
  expandedDescription?: ReactNode;
}): React.ReactElement => {
  const newClassName = clsx(styles.Text);
  if (expandedDescription) {
    return (
      <details className={clsx(newClassName, styles.Details)}>
        <summary>
          {description ? (
            <>
              {title}: <strong className={styles.Strong}>{description}</strong>{" "}
              <img
                src={chevronRight}
                alt={">"}
                className={styles.ChevronRight}
              />
            </>
          ) : (
            title
          )}
        </summary>
        <p className={styles.Strong}>{expandedDescription}</p>
      </details>
    );
  } else {
  }
  return (
    <p className={newClassName}>
      {description ? (
        <>
          {title}: <strong className={styles.Strong}>{description}</strong>
        </>
      ) : (
        title
      )}
    </p>
  );
};

export function SubscriptionCard(props: Props) {
  const { subscription, contentOnly, className } = props;
  const { t } = useTranslation();
  const { analytics } = useDI();

  const [data, setData] = useState<Subscription | undefined>(
    subscriptionPlaceholder
  );
  const [usePlaceholder, setUsePlaceholder] = useState(true);
  let navigate = useNavigate();

  const statusSettings = getStatusConfig(data?.status, data?.cancellationCause, data?.campaign, data?.expirationDate);
  const isInApp = !!sessionStorage.getItem('app')

  useEffect(() => {
    if (subscription) {
      setUsePlaceholder(false);
      setData(subscription);
    }
  }, [subscription]);

  const iconSection = (
    <Placeholder ready={!usePlaceholder}>
      <div className={styles.Icon}>
        <img
          src={nexarOneLogo}
          sizes="22px"
          width={22}
          height={22}
          alt="Nexar One Logo"
        />
      </div>
    </Placeholder>
  );

  const handleClick = () => {
    analytics?.track(statusSettings.onClickEventName);
    navigate(
      `/${APP_BASE_PATHNAMES.CONNECT}/${APP_BASE_PATHNAMES.SUBSCRIPTIONS}/${subscription.subscriptionId}`
    );
  };

  const topRightButton = statusSettings.ctaStyle === 'top-right' && (
    <div className="show-mobile">
      <Button
      block={true}
      action={handleClick}
      data-testid={`subscription-${data?.status.toLowerCase()}-open`}
      simple
    >
      {t(statusSettings.buttonTextKey)}
    </Button>
    </div>
  );

  const linkedDashCamsText =
    data?.subscriptionQuantity === 1
      ? data?.associatedDevices?.[0]?.serialNumber
      : data?.subscriptionQuantity;
  const linkedDashCamsExpandedText =
    data?.subscriptionQuantity > 1
      ? data?.associatedDevices?.map((d) => d.serialNumber).join("\n")
      : null;
  const dataSection = (
    <Placeholder ready={!usePlaceholder}>
      <div className={styles.Right}>
        <div className={styles.Header}>
          <Title>{t("nexarConnect")}</Title>
          {!isInApp && topRightButton}
        </div>
        <DescriptionItem
          title={t("status")}
          description={
            <StatusLabel
              status={data?.status}
              cancellationCause={data?.cancellationCause}
            />
          }
        />
        <DescriptionItem
          title={t("linkedDashCams")}
          description={linkedDashCamsText}
          expandedDescription={linkedDashCamsExpandedText}
        />

        {!isInApp && data?.status === "ACTIVE" && (
          <DescriptionItem
            title={t("nextBillingDate")}
            description={convertDate(data.nextBillingDate, { month: "short" })}
          />
        )}

        {data?.status === "PENDING_CANCELLATION" && (
          <DescriptionItem
            title={t("yourMembershipWillEndOn", {
              date: convertDate(data.expirationDate),
            })}
          />
        )}
      </div>
    </Placeholder>
  );

  const button = (
    <Placeholder ready={!usePlaceholder}>
      <div
        className={clsx(styles.Bottom, {
          "show-desktop": statusSettings.ctaStyle === "top-right",
        })}
      >
        <Button
          block={true}
          action={handleClick}
          alt={statusSettings.ctaStyle === "alt" || statusSettings.ctaStyle === "top-right"}
          data-testid={`subscription-${data?.status.toLowerCase()}-open`}
        >
          {t(statusSettings.buttonTextKey)}
        </Button>
      </div>
    </Placeholder>
  );

  let wrapperClassNames = clsx(
    styles.CardWrapper,
    !contentOnly && styles.BoxWrapper,
    className
  );

  return (
    <div className={wrapperClassNames}>
      <div className={styles.Top}>
        <div className={styles.Left}>{iconSection}</div>
        {dataSection}
      </div>
      {!contentOnly && !isInApp && button}
    </div>
  );
}

export default SubscriptionCard;
